import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";
import { Loader2, RefreshCw, Search, CheckCircle2, ChefHat, Truck, Clock, XCircle, Phone } from "lucide-react";

const STEPS_DELIVERY = ["en_attente", "en_cours", "en_route", "termine"];
const STEPS_PICKUP = ["en_attente", "en_cours", "termine"];
const stepsFor = (orderType) => (orderType === "pickup" ? STEPS_PICKUP : STEPS_DELIVERY);

const StepIcon = ({ step, status, steps }) => {
  const map = { en_attente: Clock, en_cours: ChefHat, en_route: Truck, termine: CheckCircle2 };
  const Icon = map[step];
  const stepIdx = steps.indexOf(step);
  const currIdx = steps.indexOf(status);
  const active = stepIdx <= currIdx && status !== "annule" && currIdx !== -1;
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${active ? "bg-[#D90429] text-white" : "bg-neutral-200 text-neutral-400"}`}>
      <Icon className="w-4 h-4" />
    </div>
  );
};

const MyOrders = () => {
  const [params, setParams] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const fetchOrders = async (emailToFetch) => {
    if (!emailToFetch) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/customer/${encodeURIComponent(emailToFetch.toLowerCase())}`);
      setOrders(data);
      setSearched(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Auto-search if email in URL or in localStorage
    const initialEmail = params.get("email") || (() => {
      try {
        const raw = localStorage.getItem("quang_customer");
        if (raw) return JSON.parse(raw).email;
      } catch (e) {}
      return "";
    })();
    if (initialEmail) {
      setEmail(initialEmail);
      fetchOrders(initialEmail);
    }
  }, []);

  // Auto refresh active orders every 15s
  useEffect(() => {
    if (!email || !searched) return;
    const hasActive = orders.some((o) => !["termine", "annule"].includes(o.status));
    if (!hasActive) return;
    const interval = setInterval(() => fetchOrders(email), 15000);
    return () => clearInterval(interval);
  }, [email, orders, searched]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!email) return;
    setParams({ email });
    fetchOrders(email);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Header />

      <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12 py-8 md:py-12">
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Mes commandes
        </h1>
        <p className="text-sm text-neutral-500 mb-6">
          Suivez vos commandes en saisissant votre adresse e-mail.
        </p>

        {/* Phone CTA */}
        <div
          data-testid="myorders-phone-cta"
          className="mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-[#D90429]/5 border-2 border-[#D90429]/20 rounded-xl px-5 py-4"
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="w-11 h-11 rounded-full bg-[#D90429] flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-neutral-900">Une question sur votre commande&nbsp;?</p>
              <p className="text-xs text-neutral-600">Notre équipe est joignable directement par téléphone.</p>
            </div>
          </div>
          <a
            href="tel:+41786162929"
            data-testid="myorders-phone-link"
            className="inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#BF0424] text-white font-black tracking-wide rounded-md px-5 h-11 text-base whitespace-nowrap transition-colors"
          >
            <Phone className="w-4 h-4" /> 078&nbsp;616&nbsp;29&nbsp;29
          </a>
        </div>

        <form onSubmit={handleSearch} className="flex gap-3 mb-8">
          <Input
            data-testid="orders-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.com"
            className="flex-1 border-neutral-300 focus-visible:ring-[#D90429] h-11"
          />
          <Button type="submit" data-testid="orders-search-btn" className="bg-[#0A0A0A] hover:bg-neutral-800 text-white rounded-md h-11 px-5">
            <Search className="w-4 h-4 mr-2" /> Rechercher
          </Button>
          {searched && (
            <Button type="button" onClick={() => fetchOrders(email)} variant="outline" className="rounded-md h-11" data-testid="orders-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          )}
        </form>

        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#D90429]" />
          </div>
        )}

        {!loading && searched && orders.length === 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-10 text-center">
            <p className="text-neutral-600 mb-4">Aucune commande trouvée pour cet e-mail.</p>
            <Link to="/commander">
              <Button className="bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md">Passer ma première commande</Button>
            </Link>
          </div>
        )}

        <div className="space-y-5">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-card-${o.id}`} className="bg-white border border-neutral-200 rounded-xl p-6">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500">Commande</p>
                  <p className="text-2xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>#{o.order_number}</p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {new Date(o.created_at).toLocaleString("fr-CH", { dateStyle: "medium", timeStyle: "short" })} ·{" "}
                    {o.order_type === "delivery" ? "Livraison" : "À emporter"}
                  </p>
                  {o.is_next_day && (
                    <span data-testid={`order-next-day-${o.id}`} className="inline-block mt-2 text-[10px] uppercase tracking-wider font-bold bg-[#E9C46A] text-black px-2 py-0.5 rounded">
                      Pour demain · {o.scheduled_date || ""} {o.scheduled_time && o.scheduled_time !== "asap" ? `· ${o.scheduled_time}` : ""}
                    </span>
                  )}
                </div>
                <StatusBadge status={o.status} data-testid={`order-status-${o.id}`} />
              </div>

              {/* Status tracker */}
              {o.status !== "annule" ? (
                <div className="flex items-center justify-between mb-5">
                  {(() => {
                    const steps = stepsFor(o.order_type);
                    return steps.map((s, i) => (
                      <React.Fragment key={s}>
                        <div className="flex flex-col items-center gap-1.5 flex-1">
                          <StepIcon step={s} status={o.status} steps={steps} />
                          <span className="text-[10px] uppercase tracking-wide font-semibold text-neutral-600 text-center">{STATUS_LABELS[s]}</span>
                        </div>
                        {i < steps.length - 1 && (
                          <div className={`h-0.5 flex-1 mx-1 -mt-5 ${steps.indexOf(o.status) > i ? "bg-[#D90429]" : "bg-neutral-200"}`} />
                        )}
                      </React.Fragment>
                    ));
                  })()}
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-5 text-neutral-500">
                  <XCircle className="w-4 h-4" />
                  <span className="text-sm">Commande annulée</span>
                </div>
              )}

              <div className="divide-y divide-neutral-100 border-t border-neutral-200 -mx-6 px-6">
                {o.items.map((it, idx) => (
                  <div key={idx} className="py-2 text-sm">
                    <div className="flex justify-between">
                      <span>{it.quantity} × {it.name}</span>
                      <span className="font-semibold">{(it.price * it.quantity).toFixed(2)} CHF</span>
                    </div>
                    {(it.selected_addons || []).length > 0 && (
                      <ul className="ml-4 text-[11px] text-neutral-500 mt-0.5">
                        {it.selected_addons.map((a, i) => (
                          <li key={i}>+ {a.name}{Number(a.price) > 0 ? ` (${Number(a.price).toFixed(2)} CHF)` : ""}</li>
                        ))}
                      </ul>
                    )}
                    {it.comment && (
                      <p className="ml-4 mt-0.5 text-[11px] italic text-neutral-600">« {it.comment} »</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-between mt-3 pt-3 border-t border-neutral-200 text-sm">
                <span className="text-neutral-600">Sous-total</span>
                <span>{o.subtotal.toFixed(2)} CHF</span>
              </div>
              {o.delivery_fee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Livraison</span>
                  <span>{o.delivery_fee.toFixed(2)} CHF</span>
                </div>
              )}
              <div className="flex justify-between font-black text-lg mt-1">
                <span>Total</span>
                <span>{o.total.toFixed(2)} CHF</span>
              </div>

              {o.note && (
                <div className="mt-4 bg-neutral-50 rounded-md p-3 text-sm border border-neutral-100">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1">Note</p>
                  {o.note}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MyOrders;
